import { MDBTable, MDBTableHead, MDBTableBody, MDBRow, MDBCol } from 'mdb-react-ui-kit';
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import html2canvas from 'html2canvas';

export default function ChimneyChart() {

    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const query = new URLSearchParams(window.location.search);
    const fileName = query.get("data") || null;
    const tenantId = query.get("tenantId") || null; // id 参数
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        async function fetchData() {
            try {
                if (tenantId && fileName) {
                    // 如果没有URL数据，尝试从API获取
                    const resp = await fetch(`/api/v1/GetChimneyChartData?tenantId=${tenantId}&fileName=${fileName}`);
                    if (!resp.ok) {
                        throw new Error(`HTTP error! status: ${resp.status}`);
                    }
                    const data = await resp.json();
                    const parsedData = JSON.parse(data.result.data);
                    setChartData(parsedData);
                    console.log('Data from API:', parsedData);
                } else {
                    //setError("No valid data source provided. Please provide either 'data' parameter or both 'tenantId' and 'fileName' parameters.");
                }
            } catch (e) {
                console.error("Error fetching data:", e);
                // setError(`Failed to load data: ${e.message}`);
            } finally {
            }
        }

        if (tenantId || fileName || searchParams.get('data')) {
            fetchData();
        } else {
        }
    }, [tenantId, fileName]);

    const handleMessage = useCallback(async (e) => {
        if (e.data?.type === 'REQUEST_SCREENSHOT') {
            try {
                // 截图当前页面
                const canvas = await html2canvas(document.body, {
                    useCORS: true,
                    scale: 2, // 高清
                    logging: false,
                });

                // 转 base64
                const base64 = canvas.toDataURL('image/png');

                // 发回给父页面
                window.parent.postMessage(
                    { type: 'SEND_SCREENSHOT', base64 },
                    '*' // 测试用 *，上线改成父页面域名
                );
            } catch (err) {
                console.error('截图失败', err);
            }
        }
    }, []);

    useEffect(() => {
        // 监听消息
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [handleMessage]);

    const formatNumberTo2Decimals = (num) => {
        const number = Number(num);
        // 非有效数字返回空（或根据需求调整）
        if (!Number.isFinite(number)) return '';
        // 先保留 2 位小数，再转为数字后转回字符串（自动去除末尾 0）
        return number.toFixed(2).toString();
    };
    const processedData = useMemo(() => {
        return chartData.map(item => {
            // 深拷贝避免修改原对象
            const newItem = { ...item };

            // 严谨判断：value不为null/undefined/空字符串（兼容0值）
            if (newItem.value != null && newItem.value !== '') {
                const formattedValue = formatNumberTo2Decimals(newItem.value);
                newItem.name = `${newItem.name}(${formattedValue}${newItem.unit || ''})`;
            }
            return newItem;
        });
    }, [chartData]);

    const factorNames = useMemo(() => {
        return Array.from(new Set(processedData.map(d => d.factorName)))
    }, [processedData])

    const publisherMap = useMemo(() => {
        const m = {}
        processedData.forEach(({ factorName, level, name, color }) => {
            if (!m[factorName]) {
                m[factorName] = {}
            }
            if (!m[factorName][level]) {
                m[factorName][level] = []
            }
            // 避免重复
            if (!m[factorName][level].some(x => x.text === name)) {
                m[factorName][level].push({ text: name, color })
            }
        })
        return m
    }, [processedData])

    // 3) 行标题：levels 0..10
    const levels = useMemo(() => Array.from({ length: 11 }, (_, i) => 10 - i), [])

    return (
        <div className="pt-4 ms-4 me-4">
            <MDBTable bordered striped>
                <MDBTableHead>
                    {
                        processedData && processedData.findIndex(d => d.type === 5) > -1 && processedData.findIndex(d => d.type !== 5) > -1 &&
                        <tr>
                            <th style={{ width: "50px", textAlign: 'center' }}></th>
                            <th style={{ textAlign: 'center' }} colspan={factorNames.length - 1}>{t("Factor")}</th>
                            <th style={{ textAlign: 'center' }}>CPPI</th>
                        </tr>
                    }
                    <tr>
                        <th style={{ width: "50px", textAlign: 'center' }}></th>

                        {
                            factorNames.map(fn => (
                                <th key={fn}
                                    style={{
                                        whiteSpace: 'normal', // 替换错误的wrap，实现自动换行
                                        textAlign: 'center',
                                        wordBreak: 'break-word', // 长文本强制换行，避免撑破maxWidth
                                        minWidth: "100px",
                                    }}>
                                    {fn}
                                </th>
                            ))
                        }
                    </tr>
                </MDBTableHead>
                <MDBTableBody>
                    {
                        levels.map(lv => (
                            <tr key={lv}>
                                <th style={{
                                    backgroundColor: "gray",
                                    textAlign: 'center', maxWidth: "50px",
                                    width: "50px",
                                    color: "white"
                                }}>
                                    {lv}
                                </th>

                                {
                                    factorNames.map(fn => {
                                        const pubs = publisherMap[fn]?.[lv] || []
                                        if (pubs.length === 0) {
                                            // 没有 publisher
                                            return (
                                                <td style={{
                                                    minWidth: "100px",  // 数据列同步min/max宽度
                                                }} key={fn + lv} />
                                            )
                                        }

                                        // 如果只有一个 publisher，直接居中显示
                                        if (pubs.length === 1) {
                                            const { text, color } = pubs[0]
                                            return (
                                                <td key={fn + lv} style={{
                                                    minWidth: "100px",  // 数据列同步min/max宽度
                                                    textAlign: 'center',
                                                    backgroundColor: color || undefined,
                                                    wordBreak: 'break-word' // 长文本换行
                                                }}>
                                                    {text}
                                                </td>
                                            )
                                        }

                                        // 多个 publisher：2 列多行展示
                                        return (
                                            <td key={fn + lv} style={{
                                                minWidth: "100px",  // 数据列同步min/max宽度
                                                padding: 0,         // 移除单元格内边距，避免嵌套元素超出
                                                wordBreak: 'break-word'
                                            }}>
                                                <MDBRow className="p-0 m-0" noGutters style={{
                                                    width: '100%',     // 撑满td宽度
                                                    height: '100%'     // 撑满td高度，避免高度塌陷
                                                }}>
                                                    {
                                                        pubs.map(({ text, color }, i) => (
                                                            <MDBCol
                                                                key={i}
                                                                md="6"
                                                                xs="12"
                                                                style={{
                                                                    textAlign: 'center',
                                                                    border: '1px solid #ccc',
                                                                    backgroundColor: color || undefined,
                                                                    paddingTop: "5px",
                                                                    paddingBottom: "5px",
                                                                    minWidth: "50px",
                                                                }}
                                                            >
                                                                {text}
                                                            </MDBCol>
                                                        ))
                                                    }
                                                </MDBRow>
                                            </td>
                                        );
                                    })
                                }
                            </tr>
                        ))
                    }
                </MDBTableBody>
            </MDBTable>
        </div >
    )
}